/**
 * Renders the assistant's markdown-light replies (bold, italics, links,
 * ordered/unordered lists, occasional headings) as MUI-styled React nodes —
 * markdown-to-jsx parses to elements, so there's no dangerouslySetInnerHTML.
 *
 * Only assistant bubbles use this; user messages stay plain text. The parser
 * tolerates partial/unclosed markdown, so it renders safely mid-stream (an
 * unclosed `**` briefly shows literal asterisks until the closing token lands).
 */

import { ReactNode } from 'react';
import Markdown from 'markdown-to-jsx';
import { Box, Link, Typography } from '@mui/material';

const Paragraph = ({ children }: { children?: ReactNode }) => (
  <Typography variant="body2" sx={{ '&:not(:last-child)': { mb: 1 }, overflowWrap: 'break-word' }}>
    {children}
  </Typography>
);

const Anchor = ({ children, href }: { children?: ReactNode; href?: string }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
    {children}
  </Link>
);

const listSx = { pl: 2.5, my: 1, '& li': { mb: 0.5 } } as const;
const UnorderedList = ({ children }: { children?: ReactNode }) => (
  <Box component="ul" sx={listSx}>
    {children}
  </Box>
);
const OrderedList = ({ children }: { children?: ReactNode }) => (
  <Box component="ol" sx={listSx}>
    {children}
  </Box>
);
const ListItem = ({ children }: { children?: ReactNode }) => (
  <Typography component="li" variant="body2" sx={{ overflowWrap: 'break-word' }}>
    {children}
  </Typography>
);

const InlineCode = ({ children }: { children?: ReactNode }) => (
  <Box
    component="code"
    sx={{
      px: 0.5,
      py: 0.125,
      borderRadius: 0.75,
      bgcolor: 'action.hover',
      fontFamily: 'monospace',
      fontSize: '0.85em',
    }}
  >
    {children}
  </Box>
);

const Heading = ({ children }: { children?: ReactNode }) => (
  <Typography
    variant="subtitle2"
    sx={{ fontWeight: 700, mt: 1, '&:not(:last-child)': { mb: 0.5 } }}
  >
    {children}
  </Typography>
);

// markdown-light persona → map every heading level to one compact bold style.
const OVERRIDES = {
  p: Paragraph,
  a: Anchor,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  code: InlineCode,
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
};

const AssistantMarkdown = ({ content }: { content: string }) => (
  <Markdown options={{ forceBlock: true, overrides: OVERRIDES }}>{content}</Markdown>
);

export default AssistantMarkdown;
